import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useOrganization } from '../context/OrganizationContext';
import { BACKEND_URL } from '../config';
import { toast } from 'sonner';
import MCPConnectionGuide from '../components/MCPConnectionGuide';

import {
  ArrowsClockwise,
  BookOpen,
  Check,
  CheckCircle,
  CircleNotch,
  Clock,
  Copy,
  Eye,
  EyeSlash,
  Globe,
  HardDrives,
  Key,
  Lightning,
  Lock,
  Plus,
  Shield,
  Trash,
  Warning,
  WarningCircle,
  XCircle
} from '@phosphor-icons/react';
/**
 * MCP Settings Page
 * Manage MCP API tokens and webhook allowlist for the workspace
 */
const MCPSettings = () => {
  const { activeOrganization } = useOrganization();
  
  // Token state
  const [tokens, setTokens] = useState([]);
  const [loadingTokens, setLoadingTokens] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showRevokeModal, setShowRevokeModal] = useState(null);
  const [createdToken, setCreatedToken] = useState(null);
  const [copiedToken, setCopiedToken] = useState(false);
  
  // Allowlist state
  const [allowlist, setAllowlist] = useState([]);
  const [loadingAllowlist, setLoadingAllowlist] = useState(true);
  const [newDomain, setNewDomain] = useState('');
  const [addingDomain, setAddingDomain] = useState(false);
  const [removingDomain, setRemovingDomain] = useState(null);
  
  // Connection guide modal
  const [showConnectionGuide, setShowConnectionGuide] = useState(false);
  
  // Active tab
  const [activeTab, setActiveTab] = useState('tokens');

  // Fetch tokens
  const fetchTokens = useCallback(async () => {
    if (!activeOrganization?.organization_id) return;
    
    try {
      setLoadingTokens(true);
      const response = await fetch(
        `${BACKEND_URL}/api/mcp/tokens`,
        {
          credentials: 'include',
          headers: {
            'X-Organization-Id': activeOrganization.organization_id
          }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setTokens(data.tokens || []);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to fetch tokens');
      }
    } catch (err) {
      console.error('Error fetching tokens:', err);
      toast.error('Failed to fetch tokens');
    } finally {
      setLoadingTokens(false);
    }
  }, [activeOrganization?.organization_id]);

  // Fetch allowlist
  const fetchAllowlist = useCallback(async () => {
    if (!activeOrganization?.organization_id) return;
    
    try {
      setLoadingAllowlist(true);
      const response = await fetch(
        `${BACKEND_URL}/api/mcp/settings/webhook-allowlist`,
        {
          credentials: 'include',
          headers: {
            'X-Organization-Id': activeOrganization.organization_id
          }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setAllowlist(data.domains || []);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to fetch allowlist');
      }
    } catch (err) {
      console.error('Error fetching allowlist:', err);
      toast.error('Failed to fetch allowlist');
    } finally {
      setLoadingAllowlist(false);
    }
  }, [activeOrganization?.organization_id]);

  useEffect(() => {
    if (activeOrganization) {
      fetchTokens();
      fetchAllowlist();
    }
  }, [activeOrganization, fetchTokens, fetchAllowlist]);

  // Revoke token
  const revokeToken = async (tokenId) => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/mcp/tokens/${tokenId}`,
        {
          method: 'DELETE',
          credentials: 'include',
          headers: {
            'X-Organization-Id': activeOrganization.organization_id
          }
        }
      );
      
      if (response.ok) {
        toast.success('Token revoked successfully');
        setTokens(tokens.filter(t => t.id !== tokenId));
        setShowRevokeModal(null);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to revoke token');
      }
    } catch (err) {
      console.error('Error revoking token:', err);
      toast.error('Failed to revoke token');
    }
  };

  // Update allowlist
  const updateAllowlist = async (newDomains) => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/mcp/settings/webhook-allowlist`,
        {
          method: 'PUT',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'X-Organization-Id': activeOrganization.organization_id
          },
          body: JSON.stringify({ domains: newDomains })
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setAllowlist(data.domains || newDomains);
        return true;
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update allowlist');
        return false;
      }
    } catch (err) {
      console.error('Error updating allowlist:', err);
      toast.error('Failed to update allowlist');
      return false;
    }
  };

  // Add domain to allowlist
  const addDomain = async () => {
    const domain = newDomain.trim().toLowerCase();
    
    // Validate domain format
    if (!domain) {
      toast.error('Please enter a domain');
      return;
    }
    
    // Remove protocol if present
    let cleanDomain = domain.replace(/^https?:\/\//, '').replace(/\/.*$/, '');
    
    // Basic domain validation
    const domainRegex = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)*$/;
    if (!domainRegex.test(cleanDomain)) {
      toast.error('Invalid domain format. Enter domain only (e.g., webhook.site)');
      return;
    }
    
    if (allowlist.includes(cleanDomain)) {
      toast.error('Domain already in allowlist');
      return;
    }
    
    setAddingDomain(true);
    const success = await updateAllowlist([...allowlist, cleanDomain]);
    if (success) {
      toast.success(`Added ${cleanDomain} to allowlist`);
      setNewDomain('');
    }
    setAddingDomain(false);
  };

  // Remove domain from allowlist
  const removeDomain = async (domain) => {
    setRemovingDomain(domain);
    const success = await updateAllowlist(allowlist.filter(d => d !== domain));
    if (success) {
      toast.success(`Removed ${domain} from allowlist`);
    }
    setRemovingDomain(null);
  };

  // Copy token to clipboard
  const copyToken = async (token) => {
    try {
      await navigator.clipboard.writeText(token);
      setCopiedToken(true);
      toast.success('Token copied to clipboard');
      setTimeout(() => setCopiedToken(false), 2000);
    } catch (err) {
      toast.error('Failed to copy token');
    }
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Scope badge colors
  const scopeColors = {
    'mcp:read': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    'agent:read': 'bg-green-500/20 text-green-400 border-green-500/30',
    'agent:run_test': 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    'agent:run_prod': 'bg-red-500/20 text-red-400 border-red-500/30',
    'webhook:send': 'bg-purple-500/20 text-purple-400 border-purple-500/30',
    'url:fetch': 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30'
  };

  if (!activeOrganization) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <CircleNotch className="w-8 h-8 animate-spin text-[#6366F1]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6 md:p-8">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <HardDrives size={32} className="text-[#6366F1]" />
              <h1 className="text-3xl font-bold text-white">MCP Settings</h1>
              <span className="px-3 py-1 bg-[#6366F1]/20 text-[#6366F1] text-xs font-semibold rounded-full border border-[#6366F1]/30">
                Developer
              </span>
            </div>
            <button
              onClick={() => setShowConnectionGuide(true)}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-emerald-500/20 to-teal-500/20 
                hover:from-emerald-500/30 hover:to-teal-500/30 text-emerald-400 rounded-xl 
                border border-emerald-500/30 transition-all group"
              data-testid="connection-guide-button"
            >
              <BookOpen size={18} className="group-hover:scale-110 transition-transform" />
              <span className="font-medium">Connection Guide</span>
              <Lightning size={14} className="text-amber-400" />
            </button>
          </div>
          <p className="text-[#A0AEC0]">
            Manage API tokens and webhook settings for MCP (Model Context Protocol) integrations
          </p>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-[rgba(255,255,255,0.1)] pb-2">
          <button
            onClick={() => setActiveTab('tokens')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
              activeTab === 'tokens'
                ? 'bg-[#6366F1] text-white'
                : 'text-gray-400 hover:text-white hover:bg-[rgba(255,255,255,0.05)]'
            }`}
          >
            <Key size={18} />
            API Tokens
          </button>
          <button
            onClick={() => setActiveTab('webhooks')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
              activeTab === 'webhooks'
                ? 'bg-[#6366F1] text-white'
                : 'text-gray-400 hover:text-white hover:bg-[rgba(255,255,255,0.05)]'
            }`}
          >
            <Globe size={18} />
            Webhook Allowlist
          </button>
        </div>

        {/* Tokens Tab */}
        {activeTab === 'tokens' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            {/* Info banner */}
            <div className="p-4 rounded-xl bg-[#6366F1]/10 border border-[#6366F1]/30">
              <div className="flex items-start gap-3">
                <Shield size={20} className="text-[#6366F1] mt-0.5" />
                <div className="flex-1">
                  <p className="text-white font-medium">MCP API Tokens</p>
                  <p className="text-sm text-[#A0AEC0] mt-1">
                    Create tokens to authenticate MCP clients (like Claude, OpenAI Responses API, or custom integrations).
                    Tokens are shown only once when created - store them securely.
                  </p>
                  <button
                    onClick={() => setShowConnectionGuide(true)}
                    className="mt-2 text-sm text-[#6366F1] hover:text-[#818cf8] font-medium flex items-center gap-1 transition-colors"
                  >
                    <BookOpen size={14} />
                    View connection instructions →
                  </button>
                </div>
              </div>
            </div>

            {/* Create Token Button */}
            <div className="flex justify-end">
              <button
                onClick={() => setShowCreateModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-[#6366F1] hover:bg-[#5855E0] text-white rounded-lg transition-colors"
              >
                <Plus size={18} />
                Create Token
              </button>
            </div>

            {/* Tokens Table */}
            <div className="rounded-xl border border-[rgba(255,255,255,0.1)] overflow-hidden">
              <div className="bg-[rgba(255,255,255,0.02)] px-4 py-3 border-b border-[rgba(255,255,255,0.1)]">
                <div className="grid grid-cols-12 gap-4 text-sm font-medium text-[#A0AEC0]">
                  <div className="col-span-3">Label</div>
                  <div className="col-span-4">Scopes</div>
                  <div className="col-span-2">Last Used</div>
                  <div className="col-span-2">Created</div>
                  <div className="col-span-1">Actions</div>
                </div>
              </div>

              {loadingTokens ? (
                <div className="p-8 text-center">
                  <CircleNotch className="w-6 h-6 animate-spin text-[#6366F1] mx-auto" />
                </div>
              ) : tokens.length === 0 ? (
                <div className="p-8 text-center text-[#A0AEC0]">
                  <Key size={32} className="mx-auto mb-3 opacity-50" />
                  <p>No API tokens yet</p>
                  <p className="text-sm mt-1">Create a token to start using MCP integrations</p>
                </div>
              ) : (
                <div className="divide-y divide-[rgba(255,255,255,0.05)]">
                  {tokens.map((token) => (
                    <div key={token.id} className="px-4 py-3 hover:bg-[rgba(255,255,255,0.02)] transition-colors">
                      <div className="grid grid-cols-12 gap-4 items-center">
                        <div className="col-span-3">
                          <div className="flex items-center gap-2">
                            <span className="text-white font-medium">{token.label}</span>
                            {!token.is_active && (
                              <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs rounded-full">
                                Revoked
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-[#A0AEC0] mt-1">
                            {token.id}
                          </div>
                        </div>
                        <div className="col-span-4 flex flex-wrap gap-1">
                          {token.scopes?.map((scope) => (
                            <span
                              key={scope}
                              className={`px-2 py-0.5 text-xs rounded border ${scopeColors[scope] || 'bg-gray-500/20 text-gray-400 border-gray-500/30'}`}
                            >
                              {scope}
                            </span>
                          ))}
                        </div>
                        <div className="col-span-2 text-sm text-[#A0AEC0]">
                          {token.last_used_at ? formatDate(token.last_used_at) : 'Never'}
                        </div>
                        <div className="col-span-2 text-sm text-[#A0AEC0]">
                          {formatDate(token.created_at)}
                        </div>
                        <div className="col-span-1">
                          {token.is_active && (
                            <button
                              onClick={() => setShowRevokeModal(token)}
                              className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                              title="Revoke token"
                            >
                              <Trash size={16} />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* Webhooks Tab */}
        {activeTab === 'webhooks' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            {/* Info banner */}
            <div className="p-4 rounded-xl bg-yellow-500/10 border border-yellow-500/30">
              <div className="flex items-start gap-3">
                <Warning size={20} className="text-yellow-400 mt-0.5" />
                <div>
                  <p className="text-white font-medium">Webhook Domain Allowlist</p>
                  <p className="text-sm text-[#A0AEC0] mt-1">
                    In production mode, <code className="px-1 py-0.5 bg-[rgba(255,255,255,0.1)] rounded">mm.send_webhook</code> calls 
                    are blocked unless the destination domain is in this allowlist. Test mode always performs dry-runs without making actual requests.
                  </p>
                </div>
              </div>
            </div>

            {/* Add Domain */}
            <div className="flex gap-3">
              <input
                type="text"
                value={newDomain}
                onChange={(e) => setNewDomain(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && addDomain()}
                placeholder="Enter domain (e.g., webhook.site, hooks.slack.com)"
                className="flex-1 px-4 py-2 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg text-white placeholder-[#A0AEC0] focus:outline-none focus:border-[#6366F1]"
              />
              <button
                onClick={addDomain}
                disabled={addingDomain || !newDomain.trim()}
                className="flex items-center gap-2 px-4 py-2 bg-[#6366F1] hover:bg-[#5855E0] disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
              >
                {addingDomain ? (
                  <CircleNotch size={18} className="animate-spin" />
                ) : (
                  <Plus size={18} />
                )}
                Add Domain
              </button>
            </div>

            {/* Allowlist */}
            <div className="rounded-xl border border-[rgba(255,255,255,0.1)] overflow-hidden">
              <div className="bg-[rgba(255,255,255,0.02)] px-4 py-3 border-b border-[rgba(255,255,255,0.1)]">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-[#A0AEC0]">Allowed Domains</span>
                  <span className="text-xs text-[#A0AEC0]">{allowlist.length} domain{allowlist.length !== 1 ? 's' : ''}</span>
                </div>
              </div>

              {loadingAllowlist ? (
                <div className="p-8 text-center">
                  <CircleNotch className="w-6 h-6 animate-spin text-[#6366F1] mx-auto" />
                </div>
              ) : allowlist.length === 0 ? (
                <div className="p-8 text-center text-[#A0AEC0]">
                  <Globe size={32} className="mx-auto mb-3 opacity-50" />
                  <p>No domains in allowlist</p>
                  <p className="text-sm mt-1">All production webhook calls will be blocked until you add allowed domains</p>
                </div>
              ) : (
                <div className="divide-y divide-[rgba(255,255,255,0.05)]">
                  {allowlist.map((domain) => (
                    <div key={domain} className="px-4 py-3 flex items-center justify-between hover:bg-[rgba(255,255,255,0.02)] transition-colors">
                      <div className="flex items-center gap-3">
                        <CheckCircle size={16} className="text-green-400" />
                        <span className="text-white font-mono">{domain}</span>
                      </div>
                      <button
                        onClick={() => removeDomain(domain)}
                        disabled={removingDomain === domain}
                        className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg transition-colors disabled:opacity-50"
                        title="Remove domain"
                      >
                        {removingDomain === domain ? (
                          <CircleNotch size={16} className="animate-spin" />
                        ) : (
                          <XCircle size={16} />
                        )}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Common domains suggestions */}
            <div className="p-4 rounded-xl bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.05)]">
              <p className="text-sm text-[#A0AEC0] mb-3">Common webhook domains:</p>
              <div className="flex flex-wrap gap-2">
                {['webhook.site', 'hooks.slack.com', 'hooks.zapier.com', 'api.pipedream.com', 'discord.com'].map((domain) => (
                  <button
                    key={domain}
                    onClick={() => !allowlist.includes(domain) && setNewDomain(domain)}
                    disabled={allowlist.includes(domain)}
                    className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                      allowlist.includes(domain)
                        ? 'bg-green-500/10 text-green-400 cursor-default'
                        : 'bg-[rgba(255,255,255,0.05)] text-[#A0AEC0] hover:bg-[rgba(255,255,255,0.1)] hover:text-white'
                    }`}
                  >
                    {allowlist.includes(domain) && <Check size={12} className="inline mr-1" />}
                    {domain}
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {/* Create Token Modal */}
        <AnimatePresence>
          {showCreateModal && (
            <CreateTokenModal
              onClose={() => {
                setShowCreateModal(false);
                setCreatedToken(null);
              }}
              onTokenCreated={(token) => {
                setCreatedToken(token);
                fetchTokens();
              }}
              createdToken={createdToken}
              onCopyToken={copyToken}
              copiedToken={copiedToken}
              organizationId={activeOrganization.organization_id}
              onShowGuide={() => setShowConnectionGuide(true)}
            />
          )}
        </AnimatePresence>

        {/* Revoke Token Modal */}
        <AnimatePresence>
          {showRevokeModal && (
            <RevokeTokenModal
              token={showRevokeModal}
              onClose={() => setShowRevokeModal(null)}
              onRevoke={() => revokeToken(showRevokeModal.id)}
            />
          )}
        </AnimatePresence>

        {/* MCP Connection Guide Modal */}
        <MCPConnectionGuide
          isOpen={showConnectionGuide}
          onClose={() => setShowConnectionGuide(false)}
          token={createdToken}
        />
      </div>
    </div>
  );
};

// Create Token Modal Component
const CreateTokenModal = ({ onClose, onTokenCreated, createdToken, onCopyToken, copiedToken, organizationId, onShowGuide }) => {
  const [label, setLabel] = useState('');
  const [selectedScopes, setSelectedScopes] = useState(['mcp:read', 'agent:read']);
  const [expiresInDays, setExpiresInDays] = useState('');
  const [creating, setCreating] = useState(false);

  const allScopes = [
    { id: 'mcp:read', label: 'MCP Read', description: 'List tools and basic MCP operations' },
    { id: 'agent:read', label: 'Agent Read', description: 'List and read agent details' },
    { id: 'agent:run_test', label: 'Agent Test', description: 'Execute agents in test mode' },
    { id: 'agent:run_prod', label: 'Agent Prod', description: 'Execute agents in production', warning: true },
    { id: 'webhook:send', label: 'Webhook Send', description: 'Send outbound webhooks' },
    { id: 'url:fetch', label: 'URL Fetch', description: 'Fetch content from URLs' }
  ];

  const toggleScope = (scope) => {
    if (selectedScopes.includes(scope)) {
      setSelectedScopes(selectedScopes.filter(s => s !== scope));
    } else {
      setSelectedScopes([...selectedScopes, scope]);
    }
  };

  const createToken = async () => {
    if (!label.trim()) {
      toast.error('Please enter a token label');
      return;
    }
    if (selectedScopes.length === 0) {
      toast.error('Please select at least one scope');
      return;
    }

    setCreating(true);
    try {
      const payload = {
        label: label.trim(),
        scopes: selectedScopes
      };
      
      if (expiresInDays && parseInt(expiresInDays) > 0) {
        payload.expires_in_days = parseInt(expiresInDays);
      }

      const response = await fetch(
        `${BACKEND_URL}/api/mcp/tokens`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'X-Organization-Id': organizationId
          },
          body: JSON.stringify(payload)
        }
      );

      if (response.ok) {
        const data = await response.json();
        toast.success('Token created successfully');
        onTokenCreated(data);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create token');
      }
    } catch (err) {
      console.error('Error creating token:', err);
      toast.error('Failed to create token');
    } finally {
      setCreating(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-[#1A1A2E] rounded-2xl border border-[rgba(255,255,255,0.1)] w-full max-w-lg max-h-[90vh] overflow-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b border-[rgba(255,255,255,0.1)]">
          <h2 className="text-xl font-bold text-white">
            {createdToken ? 'Token Created!' : 'Create MCP Token'}
          </h2>
        </div>

        <div className="p-6 space-y-6">
          {createdToken ? (
            /* Token Created View */
            <div className="space-y-4">
              <div className="p-4 rounded-xl bg-yellow-500/10 border border-yellow-500/30">
                <div className="flex items-start gap-3">
                  <Warning size={20} className="text-yellow-400 mt-0.5" />
                  <div>
                    <p className="text-white font-medium">Store this token securely</p>
                    <p className="text-sm text-[#A0AEC0] mt-1">
                      You won&apos;t be able to see this token again. Copy it now and store it in a secure location.
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-[#A0AEC0]">Your API Token</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={createdToken.token}
                    readOnly
                    className="flex-1 px-4 py-3 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg text-white font-mono text-sm"
                  />
                  <button
                    onClick={() => onCopyToken(createdToken.token)}
                    className="px-4 py-2 bg-[#6366F1] hover:bg-[#5855E0] text-white rounded-lg transition-colors flex items-center gap-2"
                  >
                    {copiedToken ? <Check size={18} /> : <Copy size={18} />}
                    {copiedToken ? 'Copied!' : 'Copy'}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-[#A0AEC0]">Label:</span>
                  <span className="text-white ml-2">{createdToken.label}</span>
                </div>
                <div>
                  <span className="text-[#A0AEC0]">Expires:</span>
                  <span className="text-white ml-2">{createdToken.expires_at ? new Date(createdToken.expires_at).toLocaleDateString() : 'Never'}</span>
                </div>
              </div>

              <div>
                <span className="text-[#A0AEC0] text-sm">Scopes:</span>
                <div className="flex flex-wrap gap-1 mt-1">
                  {createdToken.scopes.map((scope) => (
                    <span key={scope} className="px-2 py-1 bg-[#6366F1]/20 text-[#6366F1] text-xs rounded border border-[#6366F1]/30">
                      {scope}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            /* Create Token Form */
            <>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-[#A0AEC0]">Token Label *</label>
                <input
                  type="text"
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder="e.g., Claude Desktop, Production Integration"
                  className="w-full px-4 py-2 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg text-white placeholder-[#A0AEC0] focus:outline-none focus:border-[#6366F1]"
                />
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-[#A0AEC0]">Scopes *</label>
                <div className="space-y-2">
                  {allScopes.map((scope) => (
                    <label
                      key={scope.id}
                      className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        selectedScopes.includes(scope.id)
                          ? 'bg-[#6366F1]/10 border-[#6366F1]/50'
                          : 'bg-[rgba(255,255,255,0.02)] border-[rgba(255,255,255,0.1)] hover:border-[rgba(255,255,255,0.2)]'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedScopes.includes(scope.id)}
                        onChange={() => toggleScope(scope.id)}
                        className="mt-1 rounded border-gray-600 text-[#6366F1] focus:ring-[#6366F1]"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-white font-medium">{scope.label}</span>
                          {scope.warning && (
                            <Warning size={14} className="text-yellow-400" />
                          )}
                        </div>
                        <p className="text-xs text-[#A0AEC0] mt-0.5">{scope.description}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-[#A0AEC0]">Expiration (optional)</label>
                <div className="flex items-center gap-3">
                  <input
                    type="number"
                    value={expiresInDays}
                    onChange={(e) => setExpiresInDays(e.target.value)}
                    placeholder="Days until expiration"
                    min="1"
                    className="flex-1 px-4 py-2 bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg text-white placeholder-[#A0AEC0] focus:outline-none focus:border-[#6366F1]"
                  />
                  <span className="text-[#A0AEC0] text-sm">days</span>
                </div>
                <p className="text-xs text-[#A0AEC0]">Leave blank for no expiration</p>
              </div>
            </>
          )}
        </div>

        <div className="p-6 border-t border-[rgba(255,255,255,0.1)] flex justify-end gap-3">
          {createdToken ? (
            <>
              <button
                onClick={() => {
                  onClose();
                  if (onShowGuide) onShowGuide();
                }}
                className="flex items-center gap-2 px-4 py-2 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 rounded-lg border border-emerald-500/30 transition-colors"
              >
                <BookOpen size={16} />
                View Setup Guide
              </button>
              <button
                onClick={onClose}
                className="px-4 py-2 bg-[#6366F1] hover:bg-[#5855E0] text-white rounded-lg transition-colors"
              >
                Done
              </button>
            </>
          ) : (
            <>
              <button
                onClick={onClose}
                className="px-4 py-2 text-[#A0AEC0] hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={createToken}
                disabled={creating || !label.trim() || selectedScopes.length === 0}
                className="flex items-center gap-2 px-4 py-2 bg-[#6366F1] hover:bg-[#5855E0] disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
              >
                {creating && <CircleNotch size={18} className="animate-spin" />}
                Create Token
              </button>
            </>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

// Revoke Token Modal Component
const RevokeTokenModal = ({ token, onClose, onRevoke }) => {
  const [revoking, setRevoking] = useState(false);

  const handleRevoke = async () => {
    setRevoking(true);
    await onRevoke();
    setRevoking(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-[#1A1A2E] rounded-2xl border border-[rgba(255,255,255,0.1)] w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-red-500/10 rounded-full">
              <Warning size={24} className="text-red-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Revoke Token</h2>
              <p className="text-sm text-[#A0AEC0]">This action cannot be undone</p>
            </div>
          </div>

          <p className="text-[#A0AEC0] mb-4">
            Are you sure you want to revoke the token <span className="text-white font-medium">&quot;{token.label}&quot;</span>?
            Any integrations using this token will stop working immediately.
          </p>

          <div className="flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-[#A0AEC0] hover:text-white transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleRevoke}
              disabled={revoking}
              className="flex items-center gap-2 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors"
            >
              {revoking && <CircleNotch size={18} className="animate-spin" />}
              Revoke Token
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default MCPSettings;
